import { queryOptions, useQuery } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetProgramsIdPublishReadiness200 } from '@eureca/recruiter-api-sdk/types';

export type PublishReadiness = GetProgramsIdPublishReadiness200;
export type PublishIncompleteJob = PublishReadiness['incompleteJobs'][number];

/**
 * EU-450 — copy do Figma (`Home / Publish / Behavior` 15212:21203) exibida em
 * vermelho no dialog de publicação quando há vagas incompletas vinculadas.
 * O Core emite a MESMA string no 422 `incomplete_jobs` do PATCH — manter em
 * sincronia com `PUBLISH_INCOMPLETE_MESSAGE` (eureca-core program-crud).
 */
export const PUBLISH_INCOMPLETE_JOBS_MESSAGE = 'Preencha completamente as vagas para publicar';

async function fetchPublishReadiness(id: string, signal?: AbortSignal): Promise<PublishReadiness> {
  const res = await fetchClient<PublishReadiness>({
    method: 'GET',
    url: `/programs/${id}/publish-readiness`,
    signal,
  });
  if (res.error) {
    throw new Error(`Failed to load publish readiness (status ${res.error.status})`);
  }
  return res.data;
}

export function publishReadinessQueryKey(id: string) {
  return ['recruiter', 'programs', 'id', id, 'publish-readiness'] as const;
}

export function publishReadinessQueryOptions(id: string) {
  return queryOptions({
    queryKey: publishReadinessQueryKey(id),
    queryFn: ({ signal }) => fetchPublishReadiness(id, signal),
    // Sempre re-checa ao abrir o dialog — o consultor pode ter completado a
    // vaga em outra aba (o fluxo sugerido pelos links do próprio dialog).
    staleTime: 0,
  });
}

/**
 * Pre-check do dialog "Publicar" (RN-04 — a completude é decidida no backend;
 * o frontend só reflete). `enabled` deve ser o open-state do dialog para a
 * checagem rodar a cada abertura.
 */
export function usePublishReadiness(programId: string | null, enabled: boolean) {
  return useQuery({
    ...publishReadinessQueryOptions(programId ?? ''),
    enabled: enabled && Boolean(programId),
  });
}
